import { useMemo, useState } from 'react';
import { useUpdateQrCode } from '../api/useUpdateQrCode';
import {
  DEFAULT_CUSTOMIZATION,
  type QrCodeRecord,
  type QrCustomization,
} from '../api/types';
import { QrCustomizationPanel } from './QrCustomizationPanel';
import { QrPreview, downloadQr } from './QrPreview';
import { LogoUploader } from './LogoUploader';

interface Props {
  record: QrCodeRecord;
  onCancel: () => void;
  onSaved: (record: QrCodeRecord) => void;
}

const QR_BASE_URL =
  process.env.REACT_APP_QR_BASE_URL?.replace(/\/$/, '') ||
  'https://api.socialsparkapp.net/prod/qr';

interface LogoState {
  s3_key: string | null;
  preview_url: string | null;
}

export function QrCodeEdit({ record, onCancel, onSaved }: Props) {
  const isCampaign = record.type === 'campaign';
  const { update, loading } = useUpdateQrCode();

  const [name, setName] = useState(record.campaign_name || '');
  const [description, setDescription] = useState(record.description || '');
  const [startDate, setStartDate] = useState(record.start_date || '');
  const [endDate, setEndDate] = useState(record.end_date || '');
  const [customization, setCustomization] = useState<QrCustomization>(
    record.customization || DEFAULT_CUSTOMIZATION,
  );
  const [logo, setLogo] = useState<LogoState>({
    s3_key: record.logo_s3_key ?? null,
    preview_url: record.logo_url ?? null,
  });
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const qrUrl = `${QR_BASE_URL}/${record.campaign_id}`;

  const validate = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (!name.trim()) errors.name = 'Name is required';
    else if (name.length > 200) errors.name = 'Name is too long (200 char max)';
    if (description.length > 1000) errors.description = 'Description is too long (1000 char max)';
    if (isCampaign && startDate && endDate && startDate > endDate)
      errors.dates = 'Start date must be on or before end date';
    return errors;
  };

  const buildDelta = () => {
    const delta: Record<string, unknown> = { version: record.version || 1 };

    if (name.trim() !== (record.campaign_name || '').trim()) {
      delta.campaign_name = name.trim();
    }
    if (description !== (record.description || '')) {
      delta.description = description ? description : null;
    }
    if (isCampaign) {
      if (startDate !== (record.start_date || '')) {
        delta.start_date = startDate || null;
      }
      if (endDate !== (record.end_date || '')) {
        delta.end_date = endDate || null;
      }
    }
    if (JSON.stringify(customization) !== JSON.stringify(record.customization)) {
      delta.customization = customization;
    }
    if (logo.s3_key !== (record.logo_s3_key ?? null)) {
      delta.logo_s3_key = logo.s3_key;
    }
    return delta;
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    setConflict(false);

    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const delta = buildDelta();
    if (Object.keys(delta).length === 1) {
      // Only version present → nothing changed
      setSubmitError('No changes to save.');
      return;
    }

    const result = await update(record.campaign_id, delta as never);
    if (result.error) {
      if (result.error.code === 'version_conflict') {
        setConflict(true);
      } else {
        setSubmitError(result.error.message);
      }
      return;
    }
    if (result.data) onSaved(result.data);
  };

  const downloadName = useMemo(() => record.campaign_id, [record.campaign_id]);

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <button
            type="button"
            onClick={onCancel}
            className="text-xs text-gray-400 hover:text-gray-200 mb-2 inline-flex items-center gap-1"
          >
            ← Back to view
          </button>
          <h2 className="text-xl font-semibold text-white">Edit style</h2>
          <p className="text-sm text-gray-400 mt-1">
            URL stays the same: <span className="font-mono">{qrUrl}</span>. Already-printed QRs keep working.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 text-sm rounded-md border border-gray-800 text-gray-300 hover:bg-gray-800/40"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-3 py-1.5 text-sm rounded-md bg-violet-600 text-white font-medium hover:bg-violet-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </header>

      {conflict && (
        <div className="rounded-md border border-amber-900/50 bg-amber-950/30 px-3 py-2 text-sm text-amber-200">
          This QR code was changed by someone else.{' '}
          <button
            type="button"
            onClick={onCancel}
            className="underline hover:text-amber-100"
          >
            Reload to see the latest version
          </button>
          .
        </div>
      )}

      {submitError && (
        <div className="rounded-md border border-red-900/50 bg-red-950/30 px-3 py-2 text-sm text-red-300">
          {submitError}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-6">
        <div className="space-y-5">
          <div>
            <Label>{isCampaign ? 'Campaign name' : 'Display name'}</Label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={inputClass(fieldErrors.name)}
              maxLength={200}
            />
            {fieldErrors.name && <ErrorText>{fieldErrors.name}</ErrorText>}
          </div>

          {isCampaign && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Start date</Label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className={inputClass(fieldErrors.dates)}
                />
              </div>
              <div>
                <Label>End date</Label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className={inputClass(fieldErrors.dates)}
                />
              </div>
              {fieldErrors.dates && (
                <div className="col-span-2"><ErrorText>{fieldErrors.dates}</ErrorText></div>
              )}
            </div>
          )}

          <div>
            <Label>Description {isCampaign && '(optional)'}</Label>
            <input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className={inputClass(fieldErrors.description)}
              maxLength={1000}
            />
            {fieldErrors.description && <ErrorText>{fieldErrors.description}</ErrorText>}
          </div>

          <div className="pt-2 border-t border-gray-800">
            <div className="text-xs uppercase tracking-wide text-gray-500 mb-3">
              Customize QR
            </div>
            <QrCustomizationPanel
              value={customization}
              onChange={setCustomization}
              hasLogo={logo.s3_key !== null}
              logoSlot={
                <LogoUploader
                  logoUrl={logo.preview_url}
                  onChange={(next) => {
                    if (next) {
                      setLogo({ s3_key: next.s3_key, preview_url: next.preview_url });
                      if (customization.qrOptions.errorCorrectionLevel !== 'H') {
                        setCustomization((prev) => ({
                          ...prev,
                          qrOptions: { errorCorrectionLevel: 'H' },
                        }));
                      }
                    } else {
                      setLogo({ s3_key: null, preview_url: null });
                    }
                  }}
                />
              }
            />
          </div>
        </div>

        <div className="flex flex-col items-center gap-4 lg:sticky lg:top-6 self-start">
          <div className="bg-white rounded-xl p-4 shadow-lg">
            <QrPreview
              data={qrUrl}
              customization={customization}
              logoUrl={logo.preview_url ?? undefined}
              size={240}
            />
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => downloadQr(qrUrl, customization, logo.preview_url ?? undefined, downloadName, 'png')}
              className="px-3 py-1.5 text-sm rounded-md border border-gray-800 text-gray-200 hover:bg-gray-800/50"
            >
              Download PNG
            </button>
            <button
              type="button"
              onClick={() => downloadQr(qrUrl, customization, logo.preview_url ?? undefined, downloadName, 'svg')}
              className="px-3 py-1.5 text-sm rounded-md border border-gray-800 text-gray-200 hover:bg-gray-800/50"
            >
              Download SVG
            </button>
          </div>
        </div>
      </div>
    </form>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return <div className="text-xs uppercase tracking-wide text-gray-500 mb-1.5">{children}</div>;
}

function ErrorText({ children }: { children: React.ReactNode }) {
  return <div className="text-[11px] text-red-400 mt-1.5">{children}</div>;
}

function inputClass(error?: string): string {
  return [
    'w-full bg-gray-900 border rounded px-3 py-2 text-sm text-gray-100 placeholder:text-gray-600',
    'focus:outline-none focus:border-violet-500',
    error ? 'border-red-700' : 'border-gray-800',
  ].join(' ');
}
